import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useTransition } from "react";
import {
  LoadingView,
  NotificationContext,
  InvalidReasons,
  Tabs,
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import {
  useCreateIdempotencyKeys,
  fingerprintProperties,
  mutationInput,
} from "../../utils/idempotency";
import StatusDropDown from "../../dropdowns/StatusDropDown";
import ProspectEvent from "./ProspectEvent";
import SingleView from "../mapview/SingleView";
import UserDropDown from "../../dropdowns/UserDropDown";
import TagDropDown from "../../dropdowns/TagDropDown";
import TagController from "../tag/TagController";
import NotificationDropDown from "../../dropdowns/NotificationDropDown";
import { useAuth } from "../../contexts/AuthContext";

const entityQuery = gql`
  query prospectList($prospect_list_request: prospect_list_request
  $tag2Prospect_list_request: tag2Prospect_list_request
  $prospect_subscription_list_request: prospect_subscription_list_request
  ) {
    prospect(prospect_list_request: $prospect_list_request) {
      edges {
        id
        name
        status_id
        longitude
        latitude
        primary_userid
      }
      totalCount
    }
    tag2Prospect(tag2Prospect_list_request: $tag2Prospect_list_request) {
      edges {
        id
        tag_id
      }
      totalCount
    }
    prospect_subscription(prospect_subscription_list_request: $prospect_subscription_list_request) {
      edges {
        id
        user_id
      }
    }
  }
`;

const entityMutation = gql`
  mutation prospectMutate($prospect_mutation_request: MutationInput) {
    prospect(prospect_mutation_request: $prospect_mutation_request) {
      id
      isNew
    }
  }
`;

const tag2ProspectMutation = gql`
  mutation tag2ProspectMutate($tag2Prospect_mutation_request: MutationInput) {
    tag2Prospect(tag2Prospect_mutation_request: $tag2Prospect_mutation_request) {
      id
      isNew
    }
  }
`;

const prospectSubscriptionMutation = gql`
  mutation prospectSubscriptionMutate($prospect_subscription_mutation_request: MutationInput) {
    prospect_subscription(prospect_subscription_mutation_request: $prospect_subscription_mutation_request) {
      id
      isNew
    }
  }
`;

const upperCaseFirstChar = (str) => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

export default function ProspectEdit({ id: entityId, action, navigate, tab, readOnly = false }) {
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState(upperCaseFirstChar(tab) ?? 'Chat');
  const { user } = useAuth();
  // variables for entity query const
  let variables = {
    prospect_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId),
        },
      ],
    },
    tag2Prospect_list_request: {
      filters: [
        {
          field: "prospect_id",
          operator: "=",
          value: String(entityId),
        },
      ],
    },
    prospect_subscription_list_request: {
      filters: [
        {
          field: "prospect_id",
          operator: "=",
          value: String(entityId),
        },
      ],
    },
  };

  const [name, setName] = useState(null);
  const [lon, setLon] = useState(null);
  const [lat, setLat] = useState(null);
  const [status, setStatus] = useState(null);
  const [userId, setUserId] = useState(null);
  const [tagIds, setTagIds] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [location, setLocation] = useState({});
  const [userIds, setUserIds] = useState([]);
  const [subscribedUserList, setSubscribedUserList] = useState([]);

  const [invalidReasons, setInvalidReasons] = useState([]);
  const createKeys = useCreateIdempotencyKeys();
  const prospectProperties = [
    { key: "name", string: name },
    { key: "status_id", number: Number(status) },
    { key: "longitude", float: parseFloat(lon) },
    { key: "latitude", float: parseFloat(lat) },
    { key: "primary_userid", string: String(userId) },
  ];

  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    skip: !isEdit,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.prospect;
      const list = listResponse?.edges ?? [];
      const prospect = list[0];
      const tags = localData?.tag2Prospect?.edges ?? [];
      const subscribedUsers = localData?.prospect_subscription?.edges ?? [];
      setSubscribedUserList(subscribedUsers);
      setName(prospect.name);
      setStatus(prospect.status_id);
      setLon(parseFloat(prospect.longitude));
      setLat(parseFloat(prospect.latitude));
      setLocation({ lon: prospect.longitude, lat: prospect.latitude });
      setUserId(prospect.primary_userid);
      setTagIds(tags.map((tag) => tag.tag_id));
      setTagList(tags);
      setUserIds(subscribedUsers.map((item) => item.user_id))
    },
    onError: (error) => {
      console.error(error);
    },
  });
  const prospect = data?.prospect?.edges?.[0];

  const validateForm = () => {
    const reasons = [];

    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (status === null || status === undefined || status === "") reasons.push("Status is required.");
    if (lon === null || lon === undefined || lon === "") reasons.push("Longitude is required.");
    if (isNaN(Number(lon)) || Number(lon) < -180 || Number(lon) > 180) reasons.push("Longitude must be a valid number between -180 and 180.");
    if (lat === null || lat === undefined || lat === "") reasons.push("Latitude is required.");
    if (isNaN(Number(lat)) || Number(lat) < -90 || Number(lat) > 90) reasons.push("Latitude must be a valid number between -90 and 90.");
    if (userId === null || userId === undefined || userId === "") reasons.push("Primary Rep is required.");

    setInvalidReasons(reasons);
    return reasons.length === 0; return true;
  };
  // sets up Prospect mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      prospect_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: prospectProperties,
        idempotencyKey: isEdit
          ? undefined
          : createKeys.keyFor("prospect", fingerprintProperties(prospectProperties)),
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: (response, originalRequestVariables) => {
      let id = response?.prospect?.id;
      setLocation({ lon: lon == '' ? null : lon, lat: lat == '' ? null : lat });
      mutateProject(id);
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });

  const mutateProject = async (prospect_id) => {
    const tag2ProspectMutateList = [];
    const userList = [];
    tagList.forEach((tag) => {
      if (!tagIds.includes(tag.tag_id)) {
        tag2ProspectMutateList.push({ id: tag.id, tag_id: tag.tag_id, prospect_id: prospect_id });
      }
    });
    tagIds.forEach((id) => {
      if (!tagList.some((tag) => tag.tag_id == id)) {
        tag2ProspectMutateList.push({ id: 0, tag_id: id, prospect_id: prospect_id });
      }
    });
    subscribedUserList.forEach((item) => {
      if (!userIds.includes(item.user_id)) {
        userList.push({ id: item.id, user_id: item.user_id });
      }
    });
    userIds.forEach((id) => {
      if (!subscribedUserList.some((item) => item.user_id == id)) {
        userList.push({ id: 0, user_id: id });
      }
    });
    const tag2ProspectMutationPromises = tag2ProspectMutateList.map((tag) => {
      client.mutate({
        mutation: tag2ProspectMutation,
        variables: {
          tag2Prospect_mutation_request: mutationInput({
            id: tag.id > 0 ? tag.id : undefined,
            shouldDelete: tag.id > 0,
            properties: [
              { key: "tag_id", number: Number(tag.tag_id) },
              { key: "prospect_id", number: Number(tag.prospect_id) },
            ],
            idempotencyKey:
              tag.id > 0
                ? undefined
                : createKeys.keyFor(`tag:${tag.tag_id}`, `${prospect_id}:${tag.tag_id}`),
          }),
        },
      });
    })

    const subscribedUserMutationPromises = userList.map((item) => {
      client.mutate({
        mutation: prospectSubscriptionMutation,
        variables: {
          prospect_subscription_mutation_request: mutationInput({
            id: item.id > 0 ? item.id : undefined,
            shouldDelete: item.id > 0,
            properties: [
              { key: "user_id", string: String(item.user_id) },
              { key: "prospect_id", number: Number(prospect_id) },
            ],
            idempotencyKey:
              item.id > 0
                ? undefined
                : createKeys.keyFor(`sub:${item.user_id}`, `${prospect_id}:${item.user_id}`),
          }),
        },
      });
    })

    try {
      // Wait for all mutations to complete
      const results = await Promise.all([...tag2ProspectMutationPromises, ...subscribedUserMutationPromises]);
      addNotification("good", "Prospect saved successfully");

    } catch (error) {
      // Handle errors
      console.error("One or more mutations failed:", error);
      addNotification("bad", "Something went wrong");
    }
  }

  const updatePosition = (longitude, latitude) => {
    setLon(longitude);
    setLat(latitude);
  }

  const isCoordinateValid = () =>
    location.lat >= -90 && location.lat <= 90 && location.lon >= -180 && location.lon <= 180;

  let loadingMutation = mutateInfo.loading;

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          { readOnly ? "Prospect Detail" : (isEdit ? "Edit" : "Add") + " Prospect Detail" }
        </span>
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Name *:</label>
            <input value={ name } onChange={ (e) => setName(e.target.value) } disabled={ readOnly } />
          </div>
          <div className="edit-property">
            <label>Longitude *:</label>
            <input
              type="number"
              value={ lon }
              onChange={ (e) => setLon(e.target.value) }
              disabled={ readOnly }
            />
          </div>
          <div className="edit-property">
            <label>Latitude *:</label>
            <input
              type="number"
              value={ lat }
              onChange={ (e) => setLat(e.target.value) }
              disabled={ readOnly }
            />
          </div>
          <div className="edit-property">
            <label>Status *:</label>
            <StatusDropDown selected={ status } onSelect={ setStatus } disabled={ readOnly } />
          </div>
          <div className="edit-property">
            <label>Primary Rep *:</label>
            <UserDropDown selected={ userId } onSelect={ setUserId } searchEnabled disabled={ readOnly } />
          </div>
          <div className="edit-property">
            <label>Tag:</label>
            <TagDropDown
              selected={ tagIds }
              onSelect={ setTagIds }
              searchEnabled
              multiple={ true }
              entityId={ entityId }
              entityType={ 'prospect' }
              disabled={ readOnly }
            />
          </div>
          <div className="edit-property">
            <label>Assigned Technicians:</label>
            <NotificationDropDown
              selected={ userIds }
              onSelect={ setUserIds }
              searchEnabled
              multiple={ true }
              entityId={ entityId }
              entityType={ 'prospect' }
              disabled={ readOnly }
            />
          </div>
        </div>
        { invalidReasons.length > 0 && !readOnly && (
          <InvalidReasons reasons={ invalidReasons } />
        ) }
        { !readOnly && (
          <div className="edit-section">
            <SaveButton
              saving={ !isEdit && createKeys.isSaving }
              disabled={ isEdit && loadingMutation }
              onClick={ () => {
                if (!validateForm()) return;
                if (isEdit) mutateFunction();
                else createKeys.trackSave(() => mutateFunction());
              } }
            />
          </div>
        ) }

        { isEdit && (
          <Tabs autoSelect state={ [activeTab, setActiveTab] }>
            <div label="Chat">
              <ProspectEvent prospect_id={ Number(entityId) } />
            </div>
            {/* <div label="Tags">
              <StateStack path="/tag">
                {(props) => (
                  <TagController
                    {...props}
                    style={{
                      height: "600px",
                    }}
                    options={{
                      prospect_id: Number(entityId),
                    }}
                  />
                )}
              </StateStack>
            </div> */}
            <div label="Map">
              <SingleView
                lon={ isCoordinateValid() ? location.lon : null }
                lat={ isCoordinateValid() ? location.lat : null }
                type="prospect"
                updatePosition={ readOnly ? () => { } : updatePosition }
                style={ { height: "600px", position: "relative" } }
              />
            </div>
          </Tabs>
        ) }
      </div>
    </>
  );
}
